const defaultState = () => ({
  all: [],
  // { dailyLimit, remainingToday } from the list endpoint
  limits: null,
  evaluation: {},
  feedback: {},
  flashcards: {},
  session: [],
  states: {},
});

const getters = {
  all: state => state.all,
  limits: state => state.limits,
  enabled: (state, getters, rootState, rootGetters) => rootGetters['user/featureEnabled']('featureQuestionSets'),
  // The user was offered the beta and hasn't answered yet
  consentPending: (state, getters, rootState, rootGetters) => rootGetters['user/featureConsentPending']('featureQuestionSets'),
  // Show the sidebar link to users who have it on or are being offered it
  visible: (state, getters) => getters.enabled || getters.consentPending,
  evaluation: state => state.evaluation,
  // The user's thumbs up/down and comment per question id
  feedback: state => state.feedback,
  flashcards: state => state.flashcards,
  session: state => state.session,
  states: state => state.states,
};

const mutations = {
  set(state, questionSets) {
    state.all = questionSets;
  },
  setLimits(state, limits) {
    state.limits = limits || null;
  },
  setQuestionSet(state, { flashcards, questionStates, evaluation, questionFeedbacks }) {
    state.evaluation = evaluation || {};
    state.feedback = Object.fromEntries((questionFeedbacks || []).map(feedback => [feedback.questionId, feedback]));
    state.flashcards = Object.fromEntries(flashcards.map(flashcard => [flashcard.id, flashcard]));
    state.session = flashcards.map(flashcard => flashcard.id);
    state.states = Object.fromEntries(questionStates.map(questionState => [questionState.questionId, questionState]));
  },
  setState(state, questionState) {
    state.states[questionState.questionId] = questionState;
  },
  clearStates(state) {
    state.states = {};
    state.evaluation = {};
  },
  setFeedback(state, feedback) {
    state.feedback[feedback.questionId] = feedback;
  },
  setEvaluation(state, evaluation) {
    state.evaluation = evaluation || {};
  },
  reset(state) {
    Object.assign(state, defaultState());
  },
};

const actions = {
  fetchAll({ commit, rootGetters }) {
    return rootGetters['session/ajax'].get('/question_sets').then(response => {
      commit('set', response.data.questionSets);
      commit('setLimits', response.data.meta);
      return response.data.questionSets;
    });
  },
  fetch({ commit, rootGetters }, id) {
    return rootGetters['session/ajax'].get(`/question_sets/${id}`).then(response => {
      commit('setQuestionSet', response.data);
      return response.data.questionSet;
    });
  },
  // Queues generation from the prompt and any note files; the set comes back pending and fetchAll picks up the status changes
  create({ dispatch, rootGetters }, { prompt, questionCount, files = [] }) {
    const formData = new FormData();
    formData.append('prompt', prompt);
    formData.append('question_count', questionCount);
    files.forEach(file => formData.append('files[]', file));

    return rootGetters['session/ajax'].post('/question_sets', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(response => {
      dispatch('fetchAll');
      return response.data.questionSet;
    });
  },
  destroy({ dispatch, rootGetters }, id) {
    return rootGetters['session/ajax'].delete(`/question_sets/${id}`).then(() => dispatch('fetchAll'));
  },
  sendState({ commit, rootGetters }, params) {
    return rootGetters['session/ajax'].post('/question_states', params).then(response => {
      commit('setState', response.data.questionState);
      return response.data.questionState;
    });
  },
  // Saves a thumbs up/down; include comment only when the user sends one
  sendFeedback({ commit, rootGetters }, params) {
    return rootGetters['session/ajax'].post('/question_feedbacks', params).then(response => {
      commit('setFeedback', response.data.questionFeedback);
      return response.data.questionFeedback;
    });
  },
  // Asks for feedback on a finished set that has none yet or whose last attempt failed
  evaluate({ commit, rootGetters }, id) {
    return rootGetters['session/ajax'].post(`/question_sets/${id}/evaluate`).then(response => {
      commit('setEvaluation', response.data.evaluation);
      return response.data.evaluation;
    });
  },
  resetStates({ commit, rootGetters }, id) {
    return rootGetters['session/ajax'].post(`/question_sets/${id}/reset`).then(() => commit('clearStates'));
  },
};

export default {
  namespaced: true,
  state: defaultState,
  getters,
  mutations,
  actions,
};
